/* eslint-disable sciencepo-rules/require-story */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import * as bibCalendarStyle from '../../../styles/custom/bib-calendar-no-animation.module.scss';
import AppButton from '../AppButton';
import IconContainer from '../IconContainer';
import {useTranslation} from 'react-i18next';
import {getI18n} from 'react-i18next';

const fetchData = async (day, iid, lid, lang) => {
  try {
    const response = await axios.get('https://sciencespo.libcal.com/widget/hours/grid', {
      params: {
        date: day.toISOString().split('T')[0],
        format: 'json',
        lid: lid,
        iid: iid,
        weeks: 1,
        systemTime: lang
      }
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      console.error(`Server error: ${error.response.status} - ${error.response.data}`);
    } else if (error.request) {
      console.error('No response received from server');
    } else {
      console.error('Error setting up the request:', error.message);
    }
    return null;
  }
};

const BibCalendarNoAnimation = ({ locale, elem }) => {
  const {t} = useTranslation();
  const lang = getI18n().language;

  const [pages, setPages] = useState([]);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [currentDayIndex, setCurrentDayIndex] = useState(0);

  const loadPage = useCallback(async (date) => {
    try {
      const data = await fetchData(date, elem.config.iid, elem.config.lid, (lang === 'en' ? 0 : 1))
        .catch((error) => {
          console.error('Erreur lors de la récupération des données:', error.message);
          return (null);
        });
      if (!data || data.length === 0)
        return null;

      const sortWeeks = (first, second) => {
        const dateFirst = new Date(first.date).getTime();
        const dateSecond = new Date(second.date).getTime();
        return dateFirst - dateSecond;
      };

      const filterWeeks = (item, index, array) => {
        return array.findIndex(t => t.date === item.date) === index;
      };

      let calendar;
      if (!data.locations) {
        calendar = Object.values(data).map((e) => ({
          name: e.name,
          lid: e.lid,
          weeks: Object.values(e.weeks[0]).sort(sortWeeks).filter(filterWeeks)
        }));
      } else {
        calendar = data.locations.map((e) => ({
          name: e.name,
          lid: e.lid,
          weeks: Object.values(e.weeks[0]).sort(sortWeeks).filter(filterWeeks)
        }));
      }
      return calendar;
    } catch (error) {
      console.error('Erreur lors de la récupération des données:', error.message);
      return null;
    }
  }, [elem.config.iid, elem.config.lid, lang]);

  const initializePages = useCallback(async (initialDate) => {
    const prevDate = new Date(initialDate);
    prevDate.setDate(prevDate.getDate() - 7);

    const nextDate = new Date(initialDate);
    nextDate.setDate(nextDate.getDate() + 7);

    const [prevPage, currentPage, nextPage] = await Promise.all([
      loadPage(prevDate),
      loadPage(initialDate),
      loadPage(nextDate)
    ]);
    setPages([prevPage, currentPage, nextPage]);
  }, [loadPage]);

  const checkScreenSize = useCallback(() => {
    setIsSmallScreen(window.innerWidth < 768);
  }, []);

  useEffect(() => {
    initializePages(new Date());
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, [initializePages, checkScreenSize]);

  useEffect(() => {
    setCurrentDayIndex(0);
  }, [isSmallScreen]);

  const goToNextPage = useCallback(async () => {
    if (isSmallScreen) {
      if (currentDayIndex < 6) {
        setCurrentDayIndex(prevIndex => prevIndex + 1);
      } else {
        const newNextDate = new Date(pages[2][0].weeks[6].date);
        newNextDate.setDate(newNextDate.getDate() + 1);
        const newNextPage = await loadPage(newNextDate);
        setPages(prevPages => [prevPages[1], prevPages[2], newNextPage]);
        setCurrentDayIndex(0);
      }
    } else {
      const newNextDate = new Date(pages[2][0].weeks[6].date);
      newNextDate.setDate(newNextDate.getDate() + 1);
      const newNextPage = await loadPage(newNextDate);
      setPages(prevPages => [prevPages[1], prevPages[2], newNextPage]);
    }
  }, [pages, loadPage, isSmallScreen, currentDayIndex]);

  const goToPrevPage = useCallback(async () => {
    if (isSmallScreen) {
      if (currentDayIndex > 0) {
        setCurrentDayIndex(prevIndex => prevIndex - 1);
      } else {
        const newPrevDate = new Date(pages[0][0].weeks[0].date);
        newPrevDate.setDate(newPrevDate.getDate() - 1);
        const newPrevPage = await loadPage(newPrevDate);
        setPages(prevPages => [newPrevPage, prevPages[0], prevPages[1]]);
        setCurrentDayIndex(6);
      }
    } else {
      const newPrevDate = new Date(pages[0][0].weeks[0].date);
      newPrevDate.setDate(newPrevDate.getDate() - 7);
      const newPrevPage = await loadPage(newPrevDate);
      setPages(prevPages => [newPrevPage, prevPages[0], prevPages[1]]);
    }
  }, [pages, loadPage, isSmallScreen, currentDayIndex]);

  return (
    <div className={bibCalendarStyle.root}>
      {pages.length > 0 && pages[0] && pages[1] && pages[2] &&
      <>
        <div className={bibCalendarStyle.containerButton}>
          <AppButton
            color='secondary'
            leftIcon={
              <IconContainer
                color="WHITE"
                icon="ARROW_LEFT_SMALL"
                noHover
                size={14}
                variant='icon'
              />
            }
            onClick={goToPrevPage}
            padding={5}
            paddingWidth={10}
            variant="contained"
          >
            {t(isSmallScreen ? 'widget.custom.bib_calendar.before_small': 'widget.custom.bib_calendar.before')}
          </AppButton>
          <AppButton
            color='secondary'
            onClick={goToNextPage}
            padding={5}
            paddingWidth={10}
            rightIcon={
              <IconContainer
                color="WHITE"
                icon="ARROW_RIGHT_SMALL"
                noHover
                size={14}
                variant='icon'
              />
            }
            variant="contained"
          >
            {t(isSmallScreen ? 'widget.custom.bib_calendar.after_small': 'widget.custom.bib_calendar.after')}
          </AppButton>
        </div>
        <div className={bibCalendarStyle.container}>
          <table className={bibCalendarStyle.tableContainer}>
            <thead>
              <tr>
                <th/>
                {isSmallScreen ? (
                  <th key={`${currentDayIndex}-${pages[1][0].weeks[currentDayIndex].date}`}>
                    {new Date(pages[1][0].weeks[currentDayIndex].date)
                      .toLocaleDateString(locale, { weekday: 'long', month: 'short', day: 'numeric' })}
                  </th>
                ) : (
                  pages[1][0].weeks.map((e, i) => {
                    const date = new Date(e.date);
                    return (
                      <th key={`${i}-${e.date}`}>
                        {date.toLocaleDateString(locale, { month: 'short', day: 'numeric' })}
                        <br/>
                        {date.toLocaleDateString(locale, { weekday: 'long' })}
                      </th>
                    );
                  })
                )}
              </tr>
            </thead>
            <tbody>
              {pages[1].map((location, i) => (
                <tr key={`${i}_${location.name}`}>
                  <th>
                    {location.name}
                  </th>
                  {isSmallScreen ? (
                    <td key={`${currentDayIndex}-${location.weeks[currentDayIndex].rendered}`}>
                      {location.weeks[currentDayIndex].rendered}
                    </td>
                  ) : (
                    location.weeks.map((el, ind) => (
                      <td key={`${ind}-${el.rendered}`}>
                        {el.rendered}
                      </td>
                    ))
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
      }
      {
        pages?.length === 0
        &&
        <>
          <div className={bibCalendarStyle.containerButton}>
            <AppButton
              color='secondary'
              disabled
              leftIcon={
                <IconContainer
                  color="WHITE"
                  icon="ARROW_LEFT_SMALL"
                  noHover
                  size={14}
                  variant='icon'
                />
              }
              padding={5}
              paddingWidth={10}
              variant="contained"
            >
              {t(isSmallScreen ? 'widget.custom.bib_calendar.before_small': 'widget.custom.bib_calendar.before')}
            </AppButton>
            <AppButton
              color='secondary'
              disabled
              padding={5}
              paddingWidth={10}
              rightIcon={
                <IconContainer
                  color="WHITE"
                  icon="ARROW_RIGHT_SMALL"
                  noHover
                  size={14}
                  variant='icon'
                />
              }
              variant="contained"
            >
              {t(isSmallScreen ? 'widget.custom.bib_calendar.after_small': 'widget.custom.bib_calendar.after')}
            </AppButton>
          </div>
          <div className={bibCalendarStyle.containerSkeletonLoader}>
            <table className={bibCalendarStyle.skeletonLoader}>
              <thead>
                <tr>
                  <th className={bibCalendarStyle.skeletonHeader}></th>
                  {[...Array(7)].map((_, index) => (
                    <th className={bibCalendarStyle.skeletonHeader}
                      key={index}
                    >
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...Array(7)].map((_, rowIndex) => (
                  <tr key={rowIndex}>
                    <th className={bibCalendarStyle.skeletonCell}></th>
                    {[...Array(7)].map((_, cellIndex) => (
                      <td className={bibCalendarStyle.skeletonCell}
                        key={cellIndex}
                      >
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      }
      {
        pages?.length > 0
        && (!pages[0] || !pages[1] || !pages[2])
        &&
        <div className={bibCalendarStyle.diverror}>
          <p className={bibCalendarStyle.perror}>
            {t('widget.custom.bib_calendar.error')}
          </p>
          <p className={bibCalendarStyle.perrorTwo}>
            {t('widget.custom.bib_calendar.error2')}
          </p>
        </div>
      }
    </div>
  );
};

BibCalendarNoAnimation.propTypes = {
  elem : PropTypes.object.isRequired,
  locale: PropTypes.string
};

export default BibCalendarNoAnimation;